'use client'
import React, { useImperativeHandle, useRef, useState } from 'react'
import type { FaqProps, FaqItem as ItemType, FaqSemanticName } from './types.js'
import { Text, Heading } from '../../components/index.js'
import { cn } from '../../helpers/index.js'
import { withLayout } from '../../shared/Styles.js'

const Faq = React.forwardRef<HTMLDivElement, FaqProps>(
  ({ data, className, defaultOpenIndex, classNames = {} }, ref) => {
    const innerRef = useRef<HTMLDivElement>(null)
    useImperativeHandle(ref, () => innerRef.current!)

    // 将 defaultOpenIndex 转换为数组方便判断
    const openIndexes = Array.isArray(defaultOpenIndex)
      ? defaultOpenIndex
      : defaultOpenIndex !== undefined
        ? [defaultOpenIndex]
        : []

    return (
      <div ref={innerRef} className={cn(className, classNames.root)}>
        <div>
          {data?.productData?.map((item: any, index: number) => (
            <FaqItem key={index} data={item} defaultOpen={openIndexes.includes(index)} classNames={classNames} />
          ))}
        </div>
      </div>
    )
  }
)

Faq.displayName = 'Faq'

interface FaqItemProps {
  data: ItemType
  defaultOpen?: boolean
  classNames?: Partial<Record<FaqSemanticName, string>>
}

const FaqItem = ({ data, defaultOpen = false, classNames = {} }: FaqItemProps) => {
  const [open, setOpen] = useState(defaultOpen)

  const handleToggle = () => {
    setOpen(!open)
  }

  return (
    <div className={cn('tablet:py-6 border-b border-[#E4E5E6] py-4', classNames.item)}>
      <div>
        <div
          onClick={handleToggle}
          className={cn(
            'tablet:gap-[128px] flex cursor-pointer items-center justify-between gap-4',
            classNames.itemHeader
          )}
        >
          <Heading
            as="h3"
            html={data?.title}
            className={cn(
              'desktop:text-base lg-desktop:text-lg text-info-primary text-sm font-bold leading-[1.4]',
              classNames.title
            )}
          />
          <div className={cn('transition-transform duration-300', classNames.icon)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={cn('w-[20px] transition-transform duration-300', open && 'rotate-180')}
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
            >
              <path
                d="M14.4111 7.41107C14.7365 7.08563 15.264 7.08563 15.5895 7.41107C15.9149 7.73651 15.9149 8.26402 15.5895 8.58946L10.5895 13.5895C10.264 13.9149 9.73651 13.9149 9.41107 13.5895L4.41107 8.58946C4.08563 8.26402 4.08563 7.73651 4.41107 7.41107C4.73651 7.08563 5.26402 7.08563 5.58946 7.41107L10.0003 11.8219L14.4111 7.41107Z"
                fill="#1D1D1F"
              />
            </svg>
          </div>
        </div>

        {/* 带过渡的内容区域 */}
        <div
          className={cn(
            'overflow-hidden text-[#4A4C56] transition-all duration-300',
            open ? 'mt-2 max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
            classNames.content
          )}
        >
          <Text
            html={data?.desc}
            className={cn(
              'desktop:text-base lg-desktop:text-lg text-info-secondary text-sm font-bold leading-[1.4]',
              classNames.description
            )}
          />
        </div>
      </div>
    </div>
  )
}

FaqItem.displayName = 'FaqItem'

export default withLayout(Faq)
export { FaqItem }
